import React from 'react';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Plus } from 'lucide-react';
import { ApplicationSummary } from '../../types';
import ApplicationCard from './ApplicationCard';
import { COLUMN_META } from './kanbanConstants';

interface Props {
  status: keyof typeof COLUMN_META;
  label: string;
  applications: ApplicationSummary[];
  onCardClick: (application: ApplicationSummary) => void;
  onArchive: (application: ApplicationSummary) => void;
  onQuickAdd: () => void;
}

const BoardColumn: React.FC<Props> = ({ status, label, applications, onCardClick, onArchive, onQuickAdd }) => {
  const meta = COLUMN_META[status];
  const { setNodeRef, isOver } = useDroppable({ id: status });

  return (
    <div
      ref={setNodeRef}
      className={`w-[290px] shrink-0 flex flex-col rounded-card border transition-colors ${
        isOver ? 'border-brand-300 bg-brand-50/40' : 'border-zinc-200 bg-white/60'
      }`}
    >
      <div className="flex items-center gap-2 px-3 py-3 border-b border-zinc-100">
        <span className={`w-2 h-2 rounded-full ${meta.accent}`} />
        <h3 className="text-sm font-semibold text-foreground">{label}</h3>
        <span className="text-xs text-zinc-400">{applications.length}</span>
        <button
          onClick={onQuickAdd}
          className="ml-auto w-6 h-6 inline-flex items-center justify-center rounded-btn text-zinc-400 hover:text-brand-600 hover:bg-brand-50 transition-colors"
          aria-label={`Add to ${label}`}
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>

      <SortableContext
        items={applications.map((a) => `card:${a.id}`)}
        strategy={verticalListSortingStrategy}
      >
        <div className="space-y-2.5 p-2.5">
          {applications.length === 0 ? (
            <div className="flex items-center justify-center min-h-[96px] rounded-card border border-dashed border-zinc-200 text-xs text-zinc-400 text-center px-3 py-4">
              Drop a card here
            </div>
          ) : (
            applications.map((application) => (
              <ApplicationCard
                key={application.id}
                application={application}
                onClick={() => onCardClick(application)}
                onArchive={() => onArchive(application)}
              />
            ))
          )}
        </div>
      </SortableContext>
    </div>
  );
};

export default BoardColumn;